/**
 * Passkey 고수준 흐름 (Refs: #1112, Epic #1110).
 *
 * DBE Keeper(#1291) 전환: PRF 폐기. 패스키는 **신원 검증 전용**(challenge sign)이며, UMK wrap 은
 * 디바이스 키(self-ECDH) 흐름(`deviceUnlock.js`)이 담당. login(E4/E5)/update_wrapped_umk(E10) 및
 * PRF crypto 는 백엔드(PR-A)에서 제거됨에 맞춰 프론트에서도 제거.
 *
 * 외부 컴포넌트 진입점:
 *   - beginPasskeyRegistration() — ceremony 만 수행 (서버 등록 없음, #1329 클라 보관 모델)
 *   - completePasskeyRegistration({challengeId, credResponse, deviceLabel}) — 이름 확정 시 서버 등록
 *   - hasCredentials() / listCredentials() / deleteCredential() / patchCredentialLabel()
 *
 * Backend 응답: { code, message, data? }. code !== 200이면 에러 메시지 throw.
 */
import { startRegistration } from '@simplewebauthn/browser'

// 로그인/디바이스등록과 동일한 forge hashString — 서버 user.hmk 와 바이트 동일 해시 보장
import { hashString } from 'pUtils'
import {
  deleteCredential as apiDeleteCredential,
  fetchCredentials as apiFetchCredentials,
  fetchRegisterOptions,
  getHasCredentials,
  patchCredentialLabel as apiPatchCredentialLabel,
  postRegister,
  postVerifyMasterPw,
} from './api'
import { autoDetectDeviceLabel } from './deviceLabel'

// ---------------------------------------------------------------------------
// 에러 클래스
// ---------------------------------------------------------------------------

export class PasskeyApiError extends Error {
  constructor(code, message) {
    super(message || `Passkey API error: ${code}`)
    this.name = 'PasskeyApiError'
    this.code = code
  }
}

function assertOk(resp) {
  if (resp?.code !== 200) {
    throw new PasskeyApiError(resp?.code || 0, resp?.message)
  }
  return resp.data || {}
}

// ---------------------------------------------------------------------------
// Feature flag (#1117 후속) — 빌드 시점 + 환경별 분기
// ---------------------------------------------------------------------------

/**
 * Passkey 기능 활성 여부. 비활성이면 모든 Passkey UI/흐름이 차단된다.
 *
 * 결정 우선순위:
 *   1) REACT_APP_PASSKEY_ENABLED 명시 ('true' / 'false')
 *   2) 기본값: production 외 환경(dev/staging)은 ON, production은 OFF
 */
export function isPasskeyEnabled() {
  const explicit = process.env.REACT_APP_PASSKEY_ENABLED
  if (explicit === 'true') return true
  if (explicit === 'false') return false
  return true
}

// ---------------------------------------------------------------------------
// Feature detection
// ---------------------------------------------------------------------------

export function isPasskeySupported() {
  return (
    typeof window !== 'undefined' &&
    !!window.PublicKeyCredential &&
    typeof window.PublicKeyCredential === 'function'
  )
}

export async function isPlatformAuthenticatorAvailable() {
  if (!isPasskeySupported()) return false
  const fn = window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable
  if (typeof fn !== 'function') return false
  try {
    return await fn.call(window.PublicKeyCredential)
  } catch {
    return false
  }
}

// ---------------------------------------------------------------------------
// E1 + E2: beginPasskeyRegistration / completePasskeyRegistration (#1329)
// ---------------------------------------------------------------------------

/**
 * 패스키 등록 1단계 — ceremony 만 수행, 서버 등록 없음 (#1329 클라 보관 모델):
 *   1) POST /register-options → challenge + options
 *   2) navigator.credentials.create (startRegistration) → credential
 *
 * 결과(credResponse)는 호출측이 메모리에 보관했다가 이름 확정 시점에
 * completePasskeyRegistration 으로 서버 등록한다. challenge 는 Redis 5분 TTL —
 * 초과 시 complete 가 830 으로 거부되므로 호출측이 재인증을 유도한다.
 * 확정 전엔 서버에 credential 이 없어 목록에 노출되지 않는다 (이탈/취소 = 무흔적).
 *
 * @returns {Promise<{challengeId: string, credResponse: object, authenticatorAttachment: string|null}>}
 */
export async function beginPasskeyRegistration() {
  const { challenge_id: challengeId, options } = assertOk(await fetchRegisterOptions())
  const credResponse = await startRegistration({ optionsJSON: options })
  return {
    challengeId,
    credResponse,
    // 이름 입력 초기값(autoDetectDeviceLabel) 정확도용 — cross-platform 키 구분
    authenticatorAttachment: credResponse.authenticatorAttachment || null,
  }
}

/**
 * 패스키 등록 2단계 — 이름 확정 시점의 서버 등록 (#1329):
 *   POST /register {challenge_id, credential_response, device_label} → credential_id 반환
 *
 * UMK wrap 은 디바이스 키 흐름(deviceUnlock.registerCurrentDevice)이 담당하므로 여기선 불필요.
 *
 * @param {{challengeId: string, credResponse: object, deviceLabel: string}} args
 * @returns {Promise<{credentialId: string, deviceLabel: string|null, createdAt: string}>}
 */
export async function completePasskeyRegistration({ challengeId, credResponse, deviceLabel }) {
  const registerResp = assertOk(
    await postRegister({
      challenge_id: challengeId,
      credential_response: credResponse,
      device_label: deviceLabel,
    }),
  )

  return {
    credentialId: registerResp.credential_id,
    deviceLabel: registerResp.device_label,
    createdAt: registerResp.created_at,
    // PR-2 (#1301): 디바이스 자동 등록 1회용 토큰 — maybeRegisterCurrentDevice 가 소비.
    // BE 구버전이면 undefined → 호출측이 assertion 경로로 자동 fallback.
    deviceRegistrationToken: registerResp.device_registration_token,
  }
}

// ---------------------------------------------------------------------------
// 단순 조회/관리 wrappers
// ---------------------------------------------------------------------------

/**
 * E3: has-credentials — 사용자 credential 보유 여부 + 디바이스 호환성 판단용 메타.
 *
 * Issue #1154: backend 응답에 credentials_meta (aaguid + transports) 포함.
 * caller 가 디바이스 호환성을 판단하여 자동 tryPasskey skip 분기 (LoginMasterPW).
 * 등록 권유 trigger (sagas) 는 hasCredentials boolean 만 사용.
 *
 * @returns {Promise<{hasCredentials: boolean, credentialsMeta: Array<{aaguid: string|null, transports: string|null}>}>}
 */
export async function hasCredentials() {
  const resp = assertOk(await getHasCredentials())
  return {
    hasCredentials: resp.has_credentials === true,
    credentialsMeta: Array.isArray(resp.credentials_meta) ? resp.credentials_meta : [],
  }
}

export async function listCredentials() {
  const resp = assertOk(await apiFetchCredentials())
  return resp.credentials || []
}

export async function deleteCredential(credentialId) {
  const resp = await apiDeleteCredential(credentialId)
  // 835(credential not found) = 이미 삭제된 상태 — 삭제 의도가 달성됐으므로 멱등 성공 (#1319).
  // 재클릭/타 브라우저 선삭제 시 '찾을 수 없음' 에러 루프 방지 (PR-2 #1301 의 840 성공 취급과 동일 패턴)
  if (resp?.code === 835) return resp
  return assertOk(resp)
}

export async function patchCredentialLabel(credentialId, label) {
  return assertOk(await apiPatchCredentialLabel(credentialId, label))
}

export async function verifyMasterPw(hmk) {
  return assertOk(await postVerifyMasterPw(hmk))
}

/**
 * 등록 ceremony 시작 전 UMK 신선도 사전 검증 (#1509 후속).
 *
 * 배경: 마스터PW 변경 시 UMK 값 자체가 바뀌므로(UMK=비번), 다른 브라우저에서 비번을 바꾸면
 * 이 브라우저의 캐시 UMK 는 stale 이 된다. 패스키 credential 등록은 UMK 를 안 써서 성공하지만,
 * 이어지는 디바이스 등록(registerCurrentDevice)이 old_hmk 불일치로 서버 845 를 받아 실패한다
 * (credential 만 등록된 반쪽 상태). 이를 ceremony 전에 차단하기 위해 hmk=SHA256(UMK)를
 * verify-master-pw 로 대조한다.
 *
 * @param {string} umk 현재 브라우저 캐시 UMK (getUMKFromLocal 결과)
 * @returns {Promise<boolean>} 현재 마스터PW 로 확인되면 true, stale(838)이면 false.
 *   838 외 에러(네트워크 등)는 신선도 확정 불가 — 하드 차단 대신 true 로 통과시키고
 *   디바이스 등록의 845 가드를 backstop 으로 둔다.
 */
export async function isUmkFresh(umk) {
  if (!umk) return false
  try {
    await verifyMasterPw(hashString(umk))
    return true
  } catch (e) {
    if (e?.code === 838) return false // 838 = master pw mismatch = stale UMK
    // 검증 자체가 실패(네트워크/서버) — stale 로 단정하지 않고 통과 (845 가 최종 방어)
    console.warn('[passkey] isUmkFresh check failed (non-838), proceeding:', e)
    return true
  }
}

// ---------------------------------------------------------------------------
// Phase 3a #1190 후속 — 에러 분류 helper (수동 재시도 telemetry 용)
// ---------------------------------------------------------------------------

/**
 * LoginMasterPW catch 블록 (line 161-191) 의 분류 로직과 일치하는 단일 enum classifier.
 *
 * 사유: PasskeyApiError.name = 'PasskeyApiError' 가 항상 동일이라 단순 err.name 분류는
 * SAFEBOX_839 / PRF missing / 일반 backend error 를 모두 'PasskeyApiError' 로 묶어
 * 운영자가 ECS 로그에서 구분 불가. catch 블록의 5개 분기 + DOMException standard 에러 +
 * fallback 'unknown' 으로 정규화.
 *
 * backend PasskeyTelemetryManualRetryModel.error_name regex 와 1:1 매핑:
 *   ^(code_\d+(_[a-z_]+)?|NotAllowedError|SecurityError|NotSupportedError|
 *     InvalidStateError|ConstraintError|prf_missing|unknown)$
 *
 * @param {unknown} err
 * @returns {string} enum value
 */
export function classifyPasskeyError(err) {
  if (err?.code === 839) return 'code_839_stale_wrapped_umk'
  if (err?.name === 'NotAllowedError') return 'NotAllowedError'
  if (err?.code === 0 && err?.message?.includes('PRF extension result missing'))
    return 'prf_missing'
  if (err?.code && err.code !== 0) return `code_${err.code}`
  if (err?.name && err.name !== 'PasskeyApiError' && err.name !== 'Error') return err.name
  return 'unknown'
}

// re-export helpers (모달/페이지에서 직접 사용)
export { autoDetectDeviceLabel }
